import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns'
import { extractDateInTimezoneSync } from '@/utils/timezoneUtils'

export interface MealSlot {
  id: string
  date: string
  meal_type: 'breakfast' | 'lunch' | 'dinner'
  meal_name?: string
  meal_emoji?: string
  who_cooks?: string
  cook_name?: string
  notes?: string
}

export interface DayMealPlan {
  date: string
  breakfast?: MealSlot
  lunch?: MealSlot
  dinner?: MealSlot
}

export interface UnifiedEvent {
  id: string
  type: 'family' | 'school' | 'district' | 'custody' | 'availability'
  title: string
  description?: string
  date: string
  start_time?: string
  end_time?: string
  all_day: boolean
  location?: string
  event_type?: string
  school?: string
  school_id?: string
  district?: string
  district_id?: string
  child?: string
  custody_parent?: string
  early_release?: boolean
  game_type?: 'home' | 'away'
  opponent?: string
  event_subtype?: string
  jersey_color?: string
  arrival_minutes?: number
  affects_chores: boolean
  affects_transportation?: boolean
  no_school?: boolean
  remote_learning?: boolean
  needs_ride: boolean
  created_by?: string
  created_by_name?: string
  user_id?: string // For availability overrides - who the override is for
  attendees?: string[]
}

export interface CalendarDateRange {
  startDate: string
  endDate: string
}

// Query keys factory for consistent cache management
export const calendarKeys = {
  all: ['calendar'] as const,
  events: () => [...calendarKeys.all, 'events'] as const,
  eventsByRange: (range: CalendarDateRange) => [...calendarKeys.events(), range] as const,
  schools: () => [...calendarKeys.all, 'schools'] as const,
  districts: () => [...calendarKeys.all, 'districts'] as const,
  meals: () => [...calendarKeys.all, 'meals'] as const,
  mealsByRange: (range: CalendarDateRange) => [...calendarKeys.meals(), range] as const,
}

/**
 * Get date range for a month view calendar
 */
export function getMonthDateRange(date: Date): CalendarDateRange {
  const monthStart = startOfMonth(date)
  const monthEnd = endOfMonth(date)
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

  return {
    startDate: format(calendarStart, 'yyyy-MM-dd'),
    endDate: format(calendarEnd, 'yyyy-MM-dd')
  }
}

/**
 * Fetch unified calendar events with automatic caching
 */
export function useCalendarEvents(dateRange: CalendarDateRange, options?: { showCustodySchedule?: boolean }) {
  const showCustody = options?.showCustodySchedule ?? false
  return useQuery({
    queryKey: [...calendarKeys.eventsByRange(dateRange), { showCustody }],
    queryFn: async () => {
      const params: Record<string, any> = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      }
      if (showCustody) {
        params.show_custody_schedule = true
      }
      const response = await apiClient.get('/api/calendar/unified', { params })
      return (response?.events || []) as UnifiedEvent[]
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch meal plans for a date range
 */
export function useMeals(dateRange: CalendarDateRange) {
  return useQuery({
    queryKey: calendarKeys.mealsByRange(dateRange),
    queryFn: async () => {
      const response = await apiClient.get(`/api/meals/week-plan?start=${dateRange.startDate}&end=${dateRange.endDate}`)
      // API returns array of DayMealPlan objects: { date, breakfast, lunch, dinner }
      const dayPlans = Array.isArray(response) ? response : (response?.slots || response?.data || [])

      // Convert to a map for easy lookup by date
      const mealsMap = new Map<string, DayMealPlan>()
      dayPlans.forEach((plan: DayMealPlan) => {
        const dateStr = plan.date ? extractDateInTimezoneSync(plan.date) : null
        if (dateStr) {
          mealsMap.set(dateStr, plan)
        }
      })

      return mealsMap
    },
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch schools list
 */
export function useSchools() {
  return useQuery({
    queryKey: calendarKeys.schools(),
    queryFn: async () => {
      const response = await apiClient.get('/api/schools')
      return response?.schools || response || []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - schools don't change often
  })
}

/**
 * Fetch districts list
 */
export function useDistricts() {
  return useQuery({
    queryKey: calendarKeys.districts(),
    queryFn: async () => {
      const response = await apiClient.get('/api/admin/district-calendars')
      return response?.districts || response || []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Create a family event
 */
export function useCreateFamilyEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (eventData: {
      event_name: string
      event_type: string
      event_date: string
      description?: string
      start_time?: string
      end_time?: string
      all_day?: boolean
      location?: string
      affects_chores?: boolean
      needs_ride?: boolean
      attendees?: string[]
    }) => {
      return await apiClient.post('/api/events', eventData)
    },
    onSuccess: () => {
      // Invalidate all calendar queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Create a school calendar event
 */
export function useCreateSchoolEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      schoolId: string
      eventData: {
        event_name: string
        event_type: string
        event_date: string
        description?: string
        start_time?: string
        end_time?: string
        affects_transportation?: boolean
        no_school?: boolean
        early_release?: boolean
        remote_learning?: boolean
        location?: string
        game_type?: string
      }
    }) => {
      const { schoolId, eventData } = params
      return await apiClient.post(`/api/admin/schools/${schoolId}/calendar`, eventData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Create a district calendar event
 */
export function useCreateDistrictEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      districtId: string
      eventData: {
        event_name: string
        event_type: string
        event_date: string
        description?: string
        no_school?: boolean
        early_release?: boolean
        remote_learning?: boolean
      }
    }) => {
      const { districtId, eventData } = params
      return await apiClient.post(`/api/admin/district-calendars/${districtId}/events`, eventData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Update a family event
 */
export function useUpdateFamilyEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      eventId: string
      updates: Partial<{
        event_name: string
        event_type: string
        event_date: string
        description: string
        start_time: string
        end_time: string
        all_day: boolean
        location: string
        affects_chores: boolean
        needs_ride: boolean
      }>
    }) => {
      const { eventId, updates } = params
      return await apiClient.patch(`/api/events/${eventId}`, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Update a school event
 */
export function useUpdateSchoolEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      eventId: string
      updates: Partial<{
        event_name: string
        event_type: string
        event_date: string
        description: string
        start_time: string
        end_time: string
        location: string
        game_type: string
      }>
    }) => {
      const { eventId, updates } = params
      return await apiClient.patch(`/api/admin/school-events/${eventId}`, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Delete a family event
 */
export function useDeleteFamilyEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (eventId: string) => {
      return await apiClient.delete(`/api/events/${eventId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Delete a school/district event
 */
export function useDeleteSchoolEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (eventId: string) => {
      return await apiClient.delete(`/api/admin/school-events/${eventId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Delete an availability override
 */
export function useDeleteAvailabilityOverride() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (overrideId: string) => {
      return await apiClient.delete(`/api/availability/overrides/${overrideId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}

/**
 * Update an availability override
 */
export function useUpdateAvailabilityOverride() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      overrideId: string
      updates: {
        override_date: string
        override_type: string
        reason?: string
        location?: string
        all_day?: boolean
        start_time?: string
        end_time?: string
      }
    }) => {
      const { overrideId, updates } = params
      return await apiClient.patch(`/api/availability/overrides/${overrideId}`, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.events() })
    },
  })
}
